import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const TimeEntryEditModal = ({ open, onClose, onComplete, entry, prefilledData }) => {
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    duration_minutes: 60,
    entry_type: 'work',
    notes: '',
    attendees: [],
    raw_voice_input: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchProjects();
      if (entry) {
        // Edit existing entry
        // Extract date from entry.date (ISO string) - get date part only to avoid timezone shifts
        let entryDateStr;
        if (typeof entry.date === 'string') {
          if (entry.date.includes('T')) {
            entryDateStr = entry.date.split('T')[0]; // Extract YYYY-MM-DD
          } else {
            entryDateStr = entry.date;
          }
        } else {
          entryDateStr = format(new Date(entry.date), 'yyyy-MM-dd');
        }
        
        // Use start_time if available (stored in HH:MM format), otherwise calculate from date
        let startTime = '';
        let endTime = '';
        
        if (entry.start_time) {
          // Use stored start_time directly (already in HH:MM format)
          startTime = entry.start_time;
          
          // Calculate end time from start_time and duration
          const [startHour, startMin] = entry.start_time.split(':').map(Number);
          const totalMinutes = (startHour * 60 + startMin) + entry.duration_minutes;
          const endHour = Math.floor(totalMinutes / 60) % 24;
          const endMin = totalMinutes % 60;
          endTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
        } else {
          // Fallback: parse from date (for legacy entries)
          const entryDate = new Date(entry.date);
          const endDate = new Date(entryDate.getTime() + entry.duration_minutes * 60000);
          startTime = format(entryDate, 'HH:mm');
          endTime = format(endDate, 'HH:mm');
        }
        
        setFormData({
          project_id: entry.project_id || '',
          date: entryDateStr,
          start_time: startTime,
          end_time: endTime,
          duration_minutes: entry.duration_minutes,
          entry_type: entry.entry_type,
          notes: entry.notes || '',
          attendees: entry.attendees || [],
          raw_voice_input: entry.raw_voice_input || ''
        });
      } else if (prefilledData) {
        // New entry with prefilled data from calendar
        // Use prefilled data directly - it's already in correct format
        console.log('📝 TimeEntryEditModal: Received prefilledData:', prefilledData);
        
        setFormData({
          project_id: '',
          date: prefilledData.date,  // Already in "YYYY-MM-DD" format
          start_time: prefilledData.start_time,  // Already in "HH:mm" format
          end_time: prefilledData.end_time,  // Already in "HH:mm" format
          duration_minutes: prefilledData.duration_minutes || 60,
          entry_type: prefilledData.entry_type || 'work',
          notes: prefilledData.notes || '',
          attendees: [],
          raw_voice_input: ''
        });
        
        console.log('✅ Form initialized with:', {
          date: prefilledData.date,
          start_time: prefilledData.start_time,
          end_time: prefilledData.end_time,
          duration: prefilledData.duration_minutes
        });
      }
    }
  }, [open, entry, prefilledData]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const calculateDuration = () => {
    if (!formData.start_time || !formData.end_time) return;
    
    const [startHour, startMin] = formData.start_time.split(':').map(Number);
    const [endHour, endMin] = formData.end_time.split(':').map(Number);
    
    let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    if (durationMinutes < 0) durationMinutes += 24 * 60; // Next day
    
    setFormData(prev => ({ ...prev, duration_minutes: durationMinutes }));
  };

  useEffect(() => {
    calculateDuration();
  }, [formData.start_time, formData.end_time]);

  const handleSave = async () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }

    if (formData.duration_minutes <= 0) {
      toast.error('Duration must be greater than 0');
      return;
    }

    setLoading(true);
    try {
      const [hours, minutes] = formData.start_time.split(':').map(Number);
      
      // FIX: Construct date in LOCAL timezone, not UTC
      // formData.date is "YYYY-MM-DD" (e.g., "2025-10-19")
      const [year, month, day] = formData.date.split('-').map(Number);
      const entryDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      
      console.log('🐛 BUG FIX: Date construction');
      console.log('  Input date string:', formData.date);
      console.log('  Input time:', formData.start_time);
      console.log('  Constructed local date:', entryDate);
      console.log('  ISO string:', entryDate.toISOString());

      // Calculate end hour for the slot
      const endHour = hours + Math.ceil(formData.duration_minutes / 60);

      const payload = {
        project_id: formData.project_id,
        date: entryDate.toISOString(),
        duration_minutes: formData.duration_minutes,
        entry_type: formData.entry_type,
        notes: formData.notes,
        start_hour: hours,  // Store the local hour slot
        end_hour: Math.min(endHour, 23),  // Cap at 23 (11 PM)
        attendees: formData.attendees,
        raw_voice_input: formData.raw_voice_input
      };

      console.log('=== CREATING TIME ENTRY ===');
      console.log('Payload:', payload);
      console.log('Entry date (ISO):', payload.date);
      console.log('Start hour slot:', payload.start_hour);
      console.log('End hour slot:', payload.end_hour);

      let createdEntry = null;
      
      if (entry) {
        await axios.put(`${API}/time-entries/${entry.id}`, payload);
        console.log('✅ Entry updated');
        toast.success('Entry updated successfully!');
      } else {
        const response = await axios.post(`${API}/time-entries`, payload);
        createdEntry = response.data;
        
        // STEP 2: Log the created entry's hour fields
        console.log('[STEP 2] Entry created with API response:');
        console.log('  ✓ start_hour:', createdEntry.start_hour);
        console.log('  ✓ end_hour:', createdEntry.end_hour);
        console.log('  ✓ date:', createdEntry.date);
        
        toast.success('Entry created successfully!');
      }

      // Small delay to ensure backend has fully processed
      await new Promise(resolve => setTimeout(resolve, 200));
      
      console.log('Calling onComplete...');
      onComplete(createdEntry);
    } catch (error) {
      console.error('Failed to save entry:', error);
      let errorMessage = 'Failed to save time entry';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;
    
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/time-entries/${entry.id}`);
      toast.success('Entry deleted successfully!');
      onComplete();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      let errorMessage = 'Failed to delete entry';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const hours = Math.floor(formData.duration_minutes / 60);
  const mins = formData.duration_minutes % 60;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[95vw] md:max-w-[500px] max-h-[85vh] md:max-h-[80vh] overflow-y-auto shadow-2xl transition-colors" data-testid="time-entry-modal">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10 transition-colors">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors">
              {entry ? 'Edit Time Entry' : 'Add Time Entry'}
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Duration Display */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Duration</label>
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
                {hours > 0 && `${hours}h `}{mins}m
              </div>
            </div>

          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Date</label>
            <input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 transition-colors font-medium"
              data-testid="date-input"
            />
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="start-time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Start Time</label>
              <input
                id="start-time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 transition-colors font-medium"
                data-testid="start-time-input"
              />
            </div>
            <div>
              <label htmlFor="end-time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">End Time</label>
              <input
                id="end-time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 transition-colors font-medium"
                data-testid="end-time-input"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Description</label>
            <textarea
              id="notes"
              placeholder="What have you worked on?"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm placeholder:text-gray-500 dark:placeholder:text-gray-400 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 disabled:opacity-50 transition-colors font-medium"
              data-testid="notes-input"
            />
          </div>

          {/* Project */}
          <div>
            <label htmlFor="project" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Project *</label>
            <select
              id="project"
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
              className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 transition-colors font-medium"
              data-testid="project-select"
            >
              <option value="">Select project</option>
              {projects.map(proj => (
                <option key={proj.id} value={proj.id}>{proj.name}</option>
              ))}
            </select>
          </div>

          {/* Entry Type */}
          <div>
            <label htmlFor="entry-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Type</label>
            <select
              id="entry-type"
              value={formData.entry_type}
              onChange={(e) => setFormData({ ...formData, entry_type: e.target.value })}
              className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 transition-colors font-medium"
              data-testid="entry-type-select"
            >
              <option value="work">Work</option>
              <option value="meeting">Meeting</option>
              <option value="break">Break</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            {entry && (
              <button
                onClick={handleDelete}
                disabled={loading}
                data-testid="delete-button"
                className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] border border-red-200 dark:border-red-800"
              >
                Delete
              </button>
            )}
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white rounded-lg transition-all disabled:opacity-50 min-h-[44px]"
              data-testid="save-button"
            >
              {loading ? 'Saving...' : (entry ? 'Update' : 'Add')}
            </button>
          </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default TimeEntryEditModal;